"use client";

import { Flame, Sparkles, Star, TrendingUp } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { FavoriteTokenList } from "@/components/favorites/favorite-token-list";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIdentity } from "@/contexts/IdentityContext";
import { useLanguage } from "@/hooks/useLanguage";
import { useUnifiedFavoriteTokens } from "@/hooks/useUnifiedFavoriteTokens";
import { discoverSections } from "@/lib/navigation/discover";
import { cn } from "@/lib/utils";

interface TokenItem {
	id: string;
	name: string;
	ticker: string;
	price: number;
	price_6h?: number;
	created_time?: string;
}

type MarketTab = "price" | "change" | "new";

const HOT_TOKEN_IDS = ["2rfi", "2gc6", "2dzg"];

function formatSats(price: number) {
	return `${(Number(price || 0) / 1000).toFixed(3)} Sats`;
}

function getDelta(token: TokenItem) {
	const previous = Number(token.price_6h || 0);
	const current = Number(token.price || 0);
	if (!previous) return null;
	return ((current - previous) / previous) * 100;
}

async function fetchToken(tokenId: string) {
	const response = await fetch(`https://api.odin.fun/v1/token/${tokenId}`);
	if (!response.ok) return null;
	return (await response.json()) as TokenItem;
}

async function fetchMarketTokens(activeTab: MarketTab) {
	const sortMap: Record<MarketTab, string> = {
		price: "price%3Adesc",
		change: "price_delta_6h%3Adesc",
		new: "created_time%3Adesc",
	};
	const response = await fetch(
		`https://api.odin.fun/v1/tokens?page=1&limit=6&sort=${sortMap[activeTab]}`,
	);
	if (!response.ok) return [];
	const data = (await response.json()) as { data?: TokenItem[] };
	return data.data || [];
}

export function SearchDiscoveryContent({
	className,
	compact = false,
	showHotCommunities = true,
	showMarketPulse = true,
	showMoreTools = true,
}: {
	className?: string;
	compact?: boolean;
	showHotCommunities?: boolean;
	showMarketPulse?: boolean;
	showMoreTools?: boolean;
}) {
	const { language, getTranslation } = useLanguage();
	const { isAuthenticated, principal } = useIdentity();
	const [activeTab, setActiveTab] = useState<MarketTab>("price");
	const [marketTokens, setMarketTokens] = useState<TokenItem[]>([]);
	const [hotTokens, setHotTokens] = useState<TokenItem[]>([]);
	const [loading, setLoading] = useState(true);
	const { favorites, loading: loadingFavorites } = useUnifiedFavoriteTokens(
		principal,
		{
			isAuthenticated,
			enabled: true,
			limit: 5,
		},
	);

	useEffect(() => {
		let mounted = true;

		const load = async () => {
			setLoading(true);
			try {
				const [market, hot] = await Promise.all([
					showMarketPulse ? fetchMarketTokens(activeTab) : Promise.resolve([]),
					showHotCommunities
						? Promise.all(HOT_TOKEN_IDS.map((tokenId) => fetchToken(tokenId)))
						: Promise.resolve([]),
				]);

				if (!mounted) return;
				setMarketTokens(market);
				setHotTokens(hot.filter(Boolean) as TokenItem[]);
			} finally {
				if (mounted) {
					setLoading(false);
				}
			}
		};

		void load();

		return () => {
			mounted = false;
		};
	}, [activeTab, showHotCommunities, showMarketPulse]);

	const tabLabelMap = useMemo(
		() => ({
			price: language === "zh" ? "价格" : "Price",
			change: language === "zh" ? "涨幅" : "Change",
			new: language === "zh" ? "新币" : "New",
		}),
		[language],
	);

	return (
		<div className={cn("space-y-5", className)}>
			{loadingFavorites || favorites.length > 0 ? (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<Star className="h-4 w-4 text-yellow-500" />
						<h3 className="text-sm font-semibold text-foreground">
							{getTranslation("plaza.myFavorites")}
						</h3>
					</div>
					<FavoriteTokenList
						favorites={favorites}
						loading={loadingFavorites}
						itemClassName="border border-black/5 bg-white dark:border-white/10 dark:bg-[#17191d]"
					/>
				</div>
			) : null}

			{showHotCommunities ? (
				<div className="space-y-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Flame className="h-4 w-4 text-[#ff7a45]" />
						<h3 className="text-sm font-semibold text-foreground">
							{language === "zh" ? "热门社区" : "Hot Communities"}
						</h3>
					</div>
					<Link
						href="/plaza"
						className="text-xs font-medium text-light-primary dark:text-dark-primary"
					>
						{language === "zh" ? "查看广场" : "Open Plaza"}
					</Link>
				</div>
				<div className={cn("grid gap-2", compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3")}>
					{hotTokens.map((token) => (
						<Link
							key={token.id}
							href={`/plaza/token/${token.id}`}
							className="rounded-2xl border border-black/5 bg-white px-3 py-3 shadow-sm transition-colors hover:bg-black/[0.02] dark:border-white/10 dark:bg-[#17191d] dark:hover:bg-white/[0.03]"
						>
							<div className="flex items-center gap-3">
								<Image
									src={`https://images.odin.fun/token/${token.id}`}
									alt={token.name}
									width={36}
									height={36}
									className="rounded-full"
								/>
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-semibold">{token.ticker}</div>
									<div className="truncate text-xs text-muted-foreground">
										#{token.id} · {formatSats(token.price)}
									</div>
								</div>
							</div>
						</Link>
					))}
				</div>
				</div>
			) : null}

			{showMarketPulse ? (
				<div className="space-y-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<TrendingUp className="h-4 w-4 text-[#3b82f6]" />
						<h3 className="text-sm font-semibold text-foreground">
							{language === "zh" ? "市场速览" : "Market Pulse"}
						</h3>
					</div>
				</div>
				<Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as MarketTab)}>
					<TabsList className="grid h-auto grid-cols-3 rounded-full bg-black/[0.04] p-1 dark:bg-white/[0.06]">
						<TabsTrigger value="price" className="rounded-full">
							{tabLabelMap.price}
						</TabsTrigger>
						<TabsTrigger value="change" className="rounded-full">
							{tabLabelMap.change}
						</TabsTrigger>
						<TabsTrigger value="new" className="rounded-full">
							{tabLabelMap.new}
						</TabsTrigger>
					</TabsList>
				</Tabs>
				<div className={cn("grid gap-2", compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2")}>
					{loading ? (
						Array.from({ length: compact ? 3 : 4 }).map((_, index) => (
							<div
								key={index}
								className="h-16 animate-pulse rounded-2xl bg-black/[0.04] dark:bg-white/[0.06]"
							/>
						))
					) : (
						marketTokens.map((token) => {
							const delta = getDelta(token);

							return (
								<Link
									key={`${activeTab}-${token.id}`}
									href={`/plaza/token/${token.id}`}
									className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-3 py-3 shadow-sm transition-colors hover:bg-black/[0.02] dark:border-white/10 dark:bg-[#17191d] dark:hover:bg-white/[0.03]"
								>
									<Image
										src={`https://images.odin.fun/token/${token.id}`}
										alt={token.name}
										width={32}
										height={32}
										className="rounded-full"
									/>
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-semibold">{token.ticker}</div>
										<div className="truncate text-xs text-muted-foreground">
											{token.name}
										</div>
									</div>
									<div className="text-right">
										<div className="text-xs font-medium">{formatSats(token.price)}</div>
										{delta !== null ? (
											<div
												className={cn(
													"text-[11px] font-medium",
													delta >= 0 ? "text-emerald-500" : "text-rose-500",
												)}
											>
												{delta >= 0 ? "+" : ""}
												{delta.toFixed(2)}%
											</div>
										) : null}
									</div>
								</Link>
							);
						})
					)}
				</div>
				</div>
			) : null}

			{showMoreTools ? (
				<div className="space-y-3">
				<div className="flex items-center gap-2">
					<Sparkles className="h-4 w-4 text-[#a855f7]" />
					<h3 className="text-sm font-semibold text-foreground">
						{language === "zh" ? "更多功能" : "More Tools"}
					</h3>
				</div>
				<div className={cn("grid gap-3", compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2")}>
					{discoverSections.map((section) => (
						<div
							key={section.id}
							className="rounded-2xl border border-black/5 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-[#17191d]"
						>
							<div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
								{language === "zh" ? section.title : section.titleEn}
							</div>
							<div className="flex flex-wrap gap-2">
								{section.items.map((item) => (
									<Button
										key={item.id}
										asChild
										variant="outline"
										size="sm"
										className="h-8 rounded-full border-black/10 bg-transparent px-3 dark:border-white/10"
									>
										<Link href={item.href}>
											{language === "zh" ? item.name : item.nameEn}
										</Link>
									</Button>
								))}
							</div>
						</div>
					))}
				</div>
				</div>
			) : null}
		</div>
	);
}
